"use client";

import React, { useState, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArticleSummary } from "@/lib/datasets/articles";
import { getArticleImage } from "@/lib/image-mapper";

interface ArticlesHubGridProps {
  initialArticles: ArticleSummary[];
}

function parseDate(dateStr: string): Date {
  return new Date(dateStr);
}

export function ArticlesHubGrid({ initialArticles }: ArticlesHubGridProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const sortedArticles = useMemo(() => {
    return [...initialArticles].sort((a, b) => {
      if (a.isNew && !b.isNew) return -1;
      if (!a.isNew && b.isNew) return 1;
      const dateA = parseDate(a.createdAt).getTime();
      const dateB = parseDate(b.createdAt).getTime();
      return dateB - dateA;
    });
  }, [initialArticles]);

  const categories = useMemo(() => {
    return ["All", ...Array.from(new Set(sortedArticles.map((a) => a.category)))];
  }, [sortedArticles]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { All: sortedArticles.length };
    for (const cat of categories) {
      if (cat !== "All") {
        counts[cat] = sortedArticles.filter((a) => a.category === cat).length;
      }
    }
    return counts;
  }, [sortedArticles, categories]);

  const filteredArticles = useMemo(() => {
    return sortedArticles.filter((article) => {
      const matchesCategory = selectedCategory === "All" || article.category === selectedCategory;
      const matchesSearch =
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (article.tags && article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())));
      return matchesCategory && matchesSearch;
    });
  }, [sortedArticles, selectedCategory, searchQuery]);

  return (
    <div className="w-full flex flex-col gap-8 text-left">
      
      {/* Search & Category Filter Section */}
      <div className="w-full flex flex-col md:flex-row gap-6 items-center justify-between py-6 border-y border-[var(--line-strong)]">
        
        {/* Category Pill Buttons */}
        <div role="tablist" className="flex flex-wrap gap-2 items-center w-full md:w-auto justify-start font-mono text-[10px] uppercase tracking-wider">
          {categories.map((category) => {
            const isActive = selectedCategory === category;
            const count = categoryCounts[category] ?? 0;
            const catId = category.replace(/\s+/g, "-").toLowerCase();
            return (
              <button
                key={category}
                role="tab"
                aria-selected={isActive}
                aria-controls="articles-grid"
                id={`tab-art-${catId}`}
                onClick={() => setSelectedCategory(category)}
                className={`h-8 px-4 font-bold rounded transition-all border cursor-pointer ${
                  isActive
                    ? "bg-[var(--accent)] text-white border-[var(--accent)] shadow-none"
                    : "bg-[var(--surface-muted)] text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
                }`}
              >
                {category}
                <span className={`ml-1.5 text-[9px] font-semibold opacity-60`}>
                  ({count})
                </span>
              </button>
            );
          })}
        </div>

        {/* Search input field */}
        <div className="flex items-center gap-3 w-full md:w-auto">
          <span className="text-[10px] font-mono text-[var(--ink-faint)] whitespace-nowrap uppercase tracking-wider">
            {filteredArticles.length} Guide{filteredArticles.length !== 1 ? "s" : ""}
          </span>
          <div className="relative w-full md:w-64">
            <input
              type="text"
              placeholder="Search guides..."
              value={searchQuery}
              aria-label="Search guides"
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-3 pr-8 py-2 text-xs font-mono border rounded bg-[var(--surface-muted)] text-[var(--ink)]"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                aria-label="Clear search query"
                className="absolute inset-y-0 right-2 flex items-center text-[var(--ink-faint)] hover:text-[var(--ink)] cursor-pointer"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Grid listing */}
      {filteredArticles.length > 0 ? (
        <div
          id="articles-grid"
          role="tabpanel"
          aria-labelledby={`tab-art-${selectedCategory.replace(/\s+/g, "-").toLowerCase()}`}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {filteredArticles.map((article, index) => {
            const isNew = !!article.isNew;
            const articleImage = getArticleImage(article.slug);
            return (
              <article key={article.slug} className="h-full">
                <Link
                  href={`/articles/${article.slug}/`}
                  className="group flex flex-col h-full bg-[var(--surface)] border border-[var(--border)] rounded p-6 card-blueprint hover:border-[var(--accent)] transition-all duration-150 relative text-left"
                >
                  {/* Article Header Image */}
                  <div className="relative w-full aspect-[16/10] rounded overflow-hidden mb-4 bg-[var(--surface-muted)] border border-[var(--border)]">
                    <Image
                      src={articleImage}
                      alt={article.title}
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 360px"
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                      priority={isNew || index < 3}
                    />
                  </div>

                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[9px] font-mono font-bold uppercase tracking-widest text-[var(--accent)]">
                      {article.category}
                    </span>
                    {isNew && (
                      <span className="text-[8px] font-mono font-bold uppercase tracking-widest text-[var(--rust)] bg-[var(--rust-tint)] px-1.5 py-0.5 rounded border border-[var(--rust)] border-dotted">
                        New
                      </span>
                    )}
                  </div>

                  <h3 className="text-base font-extrabold text-[var(--ink)] group-hover:text-[var(--accent)] tracking-tight mb-2 line-clamp-2 font-sans transition-colors leading-tight uppercase">
                    {article.title}
                  </h3>

                  <p className="text-[var(--ink-soft)] text-xs leading-relaxed line-clamp-3 font-sans flex-1">
                    {article.description}
                  </p>

                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-[var(--line)] text-[10px] font-mono text-[var(--ink-soft)] uppercase tracking-wider">
                    <span>{article.readTime}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-[var(--ink-soft)] group-hover:text-[var(--accent)] group-hover:translate-x-0.5 transition-all">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </div>
                </Link>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center text-center py-16 px-4 bg-[var(--surface-muted)] border border-dashed border-[var(--border)] rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-[var(--ink-faint)] mb-3">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
          </svg>
          <h4 className="font-heading-condensed font-extrabold text-lg text-[var(--ink)] leading-none mb-2 uppercase">
            No Guides Found
          </h4>
          <p className="text-xs text-[var(--ink-soft)] max-w-xs leading-relaxed font-sans">
            Try searching for a different term or category.
          </p>
        </div>
      )}
    </div>
  );
}
